import { AlertColor, Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import DrawerMenu from '../../../../../../../components/DrawerMenu';
import { Dispatch, useCallback, useMemo, useState } from 'react';
import strings from '../../../../../../../common/Translation/Translate';
import Notification from '../../../../../../../components/Notification';
import { getDrawerWidth } from '../../../../../../../utils/validators/HelperFunctions';
import { VenueConfigSectionAction } from '../../../VenueConfigSectionAction';
import { MappingLayoutCards, VenueConfigLayoutItem } from './MappingLayoutCards';
import {
  buildConfiguredByLayoutId,
  getRowLayoutId,
  isCapacityComplete,
  mergeLayoutItemsWithConfigured,
  validateCapacity,
} from './layoutMappingUtils';

type AddEditMappingLayoutProps = {
  editedData: any;
  setData: Dispatch<any>;
  dropdownValue: any;
  layoutItems?: VenueConfigLayoutItem[];
};

export const AddEditMappingLayout: React.FC<AddEditMappingLayoutProps> = ({
  editedData = [],
  setData,
  dropdownValue,
  layoutItems: layoutItemsProp,
}) => {
  const [capacityErrors, setCapacityErrors] = useState<Record<number, string>>({});
  const [layoutDrawerOpen, setLayoutDrawerOpen] = useState(false);
  const [drawerLayout, setDrawerLayout] = useState<{ value: number; label: string } | null>(null);
  const [drawerMinCapacity, setDrawerMinCapacity] = useState('');
  const [drawerMaxCapacity, setDrawerMaxCapacity] = useState('');
  const [drawerError, setDrawerError] = useState('');
  const [messageMappingLayout, setmessageMappingLayout] = useState<{
    display: boolean;
    severity: AlertColor | null;
    messageMappingLayout: string;
  }>({
    display: false,
    severity: null,
    messageMappingLayout: '',
  });

  const masterLayoutItems: VenueConfigLayoutItem[] =
    layoutItemsProp ?? dropdownValue?.layoutItems ?? [];

  const allLayoutItems = useMemo(
    () => mergeLayoutItemsWithConfigured(masterLayoutItems, editedData),
    [masterLayoutItems, editedData],
  );

  const layoutSelectOptions = useMemo(() => {
    if (dropdownValue?.layouts?.length) {
      return dropdownValue.layouts.map((opt: any) => ({
        value: Number(opt.value ?? opt.id),
        label: String(opt.label ?? opt.name ?? opt.value),
      }));
    }
    return allLayoutItems.map(item => ({ value: Number(item.id), label: item.name }));
  }, [dropdownValue?.layouts, allLayoutItems]);

  const closeLayoutDrawer = () => {
    setLayoutDrawerOpen(false);
    setDrawerLayout(null);
    setDrawerMinCapacity('');
    setDrawerMaxCapacity('');
    setDrawerError('');
  };

  const handleSaveLayoutDrawer = () => {
    if (!drawerLayout) {
      setDrawerError(strings.requiredText);
      return;
    }
    const error = validateCapacity(drawerMinCapacity, drawerMaxCapacity);
    if (error) {
      setDrawerError(error);
      return;
    }
    upsertLayoutEntry(drawerLayout.value, drawerLayout.label, drawerMinCapacity, drawerMaxCapacity);
    setCapacityErrorForLayout(drawerLayout.value, drawerMinCapacity, drawerMaxCapacity);
    closeLayoutDrawer();
    requestAnimationFrame(() => {
      document.getElementById(`venue-config-layout-${drawerLayout.value}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  };

  const closeNotification = (value: boolean) => {
    setmessageMappingLayout({ ...messageMappingLayout, display: value });
  };

  const setCapacityErrorForLayout = useCallback((layoutId: number, min: any, max: any) => {
    const minStr = String(min ?? '').trim();
    const maxStr = String(max ?? '').trim();
    if (!minStr && !maxStr) {
      setCapacityErrors(prev => {
        const next = { ...prev };
        delete next[layoutId];
        return next;
      });
      return '';
    }
    const error = validateCapacity(min, max);
    setCapacityErrors(prev => {
      const next = { ...prev };
      if (error) {
        next[layoutId] = error;
      } else {
        delete next[layoutId];
      }
      return next;
    });
    return error;
  }, []);

  const upsertLayoutEntry = (
    layoutId: number,
    layoutName: string,
    minCapacity: string,
    maxCapacity: string,
  ) => {
    const minStr = String(minCapacity ?? '').trim();
    const maxStr = String(maxCapacity ?? '').trim();

    setData((prev: any[]) => {
      if (!minStr && !maxStr) {
        return prev.filter((row: any) => getRowLayoutId(row) !== layoutId);
      }

      const entry = {
        layouts: { value: layoutId, label: layoutName },
        layoutId,
        layoutName,
        minCapacity: minCapacity ?? '',
        maxCapacity: maxCapacity ?? '',
      };

      const existingIdx = prev.findIndex((row: any) => getRowLayoutId(row) === layoutId);
      if (existingIdx >= 0) {
        const array = [...prev];
        array[existingIdx] = { ...array[existingIdx], ...entry };
        return array;
      }
      return [...prev, entry];
    });
  };

  const handleCapacityChange = (
    layoutId: number,
    layoutName: string,
    field: 'minCapacity' | 'maxCapacity',
    value: string,
  ) => {
    const configured = buildConfiguredByLayoutId(editedData).get(layoutId);
    const minCapacity = field === 'minCapacity' ? value : (configured?.minCapacity ?? '');
    const maxCapacity = field === 'maxCapacity' ? value : (configured?.maxCapacity ?? '');
    upsertLayoutEntry(layoutId, layoutName, minCapacity, maxCapacity);
    setCapacityErrorForLayout(layoutId, minCapacity, maxCapacity);
  };

  const handleCapacityBlur = (layoutId: number) => {
    const configured = buildConfiguredByLayoutId(editedData).get(layoutId);
    if (!configured) return;

    const error = setCapacityErrorForLayout(layoutId, configured.minCapacity, configured.maxCapacity);
    if (error) {
      setmessageMappingLayout({
        display: true,
        severity: 'error',
        messageMappingLayout: error,
      });
      return;
    }

    if (!isCapacityComplete(configured.minCapacity, configured.maxCapacity)) {
      setData((prev: any[]) => prev.filter((row: any) => getRowLayoutId(row) !== layoutId));
    }
  };

  const handleClear = (layoutId: number) => {
    setData((prev: any[]) => prev.filter((row: any) => getRowLayoutId(row) !== layoutId));
    setCapacityErrors(prev => {
      const next = { ...prev };
      delete next[layoutId];
      return next;
    });
  };

  return (
    <>
      {messageMappingLayout.display && (
        <Notification
          isOpen={messageMappingLayout.display}
          message={messageMappingLayout.messageMappingLayout}
          severity={messageMappingLayout.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <Box sx={{ mb: 1 }}>
        <VenueConfigSectionAction
          label={strings.venueConfig_mapping_add_layout}
          onClick={() => setLayoutDrawerOpen(true)}
        />
      </Box>
      <MappingLayoutCards
        layoutItems={allLayoutItems}
        editedData={editedData}
        capacityErrors={capacityErrors}
        onCapacityChange={handleCapacityChange}
        onCapacityBlur={handleCapacityBlur}
        onClear={handleClear}
      />
      <DrawerMenu
        width={getDrawerWidth(false, false, true)}
        open={layoutDrawerOpen}
        handleClose={closeLayoutDrawer}
        closeButton
        title={strings.venueConfig_mapping_add_layout}>
        <Box display="flex" flexDirection="column" gap={2} sx={{ p: 2 }}>
          <Autocomplete
            options={layoutSelectOptions}
            getOptionLabel={opt => opt.label}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={drawerLayout}
            onChange={(_, value) => {
              setDrawerLayout(value);
              setDrawerError('');
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={strings.venueConfig_mapping_select_layout}
                required
                error={!drawerLayout && Boolean(drawerError)}
              />
            )}
          />
          <TextField
            label={strings.venueConfig_mapping_minCapacity}
            type="number"
            size="small"
            value={drawerMinCapacity}
            onChange={e => setDrawerMinCapacity(e.target.value)}
            inputProps={{ min: 1 }}
          />
          <TextField
            label={strings.venueConfig_mapping_maxCapacity}
            type="number"
            size="small"
            value={drawerMaxCapacity}
            onChange={e => setDrawerMaxCapacity(e.target.value)}
            inputProps={{ min: 1 }}
          />
          {drawerError && (
            <Typography variant="caption" color="error">
              {drawerError}
            </Typography>
          )}
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={closeLayoutDrawer}>
              {strings.cancelText}
            </Button>
            <Button variant="contained" onClick={handleSaveLayoutDrawer}>
              {strings.common_button_save}
            </Button>
          </Box>
        </Box>
      </DrawerMenu>
    </>
  );
};
